import { useEffect, useRef } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Plus, Settings2 } from 'lucide-react';
import {
  Switch, Button,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import { useCustomerContracts } from '@/api/customer-contracts';
import { InputField, SelectField } from '../form-controls';
import { FieldGrid } from '../section-shell';
import { CURRENCY_OPTIONS, type ContractFormValues } from '../contract-form';
import { usePartnerOptions } from './partner-options';

/**
 * Custom configuration — currency + exchange rate, plus the supplier/parent
 * linkage. `use_contract_id` on: pick a parent contract of this customer (its
 * supplier auto-populates `partner_id`); off: an optional supplier picker.
 * Mirrors legacy CustomConfigSection. The supplier-create action is a stub
 * (a full create modal is out of scope for this form).
 */
export function CustomConfigSection({ customerId }: { customerId: number }) {
  const { control, setValue } = useFormContext<ContractFormValues>();
  const toast = useToast();

  const useContractId = useWatch({ control, name: 'use_contract_id' });
  const contractId = useWatch({ control, name: 'contract_id' });

  const { data: partners = [] } = usePartnerOptions();
  const { data: parentContractsRes } = useCustomerContracts(
    useContractId ? customerId : 0,
    { page_size: 100 },
  );
  const parentContracts = parentContractsRes?.results ?? [];

  const partnerOptions = partners.map((p) => ({
    value: String(p.partner_id),
    label: p.partner_code ? `${p.partner_name} (${p.partner_code})` : p.partner_name,
  }));

  // When a parent contract is picked, copy its supplier onto partner_id (legacy sync).
  const syncedRef = useRef<number>(0);
  useEffect(() => {
    if (!useContractId) {
      syncedRef.current = 0;
      return;
    }
    if (!contractId || syncedRef.current === contractId) return;
    const parent = parentContracts.find((c) => c.contract_id === contractId);
    if (!parent) return;
    syncedRef.current = contractId;
    setValue('partner_id', parent.partner_id ?? 0, { shouldValidate: true });
  }, [useContractId, contractId, parentContracts, setValue]);

  const onCreateSupplier = () => {
    toast.warning('Create a supplier from the Partners area, then pick it here.');
  };

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <FieldGrid>
        <SelectField
          name="currency_code"
          label="Currency"
          options={CURRENCY_OPTIONS}
          placeholder="Select currency"
        />
        <InputField
          name="exchange_rate"
          label="Exchange rate"
          type="number"
          placeholder="1"
          description="Rate to base currency."
        />
      </FieldGrid>

      <Controller
        control={control}
        name="use_contract_id"
        render={({ field }) => (
          <label style={{ display: 'flex', gap: 'var(--vn-space-3)', alignItems: 'center', cursor: 'pointer' }}>
            <Switch
              checked={!!field.value}
              onCheckedChange={(checked) => {
                field.onChange(checked);
                if (!checked) setValue('contract_id', 0);
              }}
              aria-label="Link to a parent contract"
            />
            <span style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center', fontSize: 'var(--vn-font-size-sm)' }}>
              <Settings2 size={14} style={{ color: 'var(--vn-fg-muted)' }} />
              Link to an existing contract
            </span>
          </label>
        )}
      />

      {useContractId ? (
        <Field label="Parent contract" description="The supplier is copied from the selected contract.">
          {(p) => (
            <Select
              value={contractId > 0 ? String(contractId) : ''}
              onValueChange={(v) => setValue('contract_id', Number(v))}
              disabled={!customerId}
            >
              <SelectTrigger {...p} disabled={!customerId}>
                <SelectValue placeholder={customerId ? 'Select a contract' : 'Select a customer first'} />
              </SelectTrigger>
              <SelectContent>
                {parentContracts.map((c) => (
                  <SelectItem key={c.contract_id} value={String(c.contract_id)}>
                    {c.contract_number_id}
                    {c.product_group_name ? ` — ${c.product_group_name}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>
      ) : null}

      <Controller
        control={control}
        name="partner_id"
        render={({ field }) => (
          <Field label="Supplier">
            {(p) => (
              <Select
                value={field.value > 0 ? String(field.value) : ''}
                onValueChange={(v) => field.onChange(Number(v))}
              >
                <SelectTrigger {...p}>
                  <SelectValue placeholder="Select a supplier (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {partnerOptions.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
        )}
      />

      <Button
        type="button"
        variant="secondary"
        size="sm"
        startIcon={<Plus size={14} />}
        onClick={onCreateSupplier}
        style={{ justifySelf: 'start' }}
      >
        Create supplier
      </Button>
    </div>
  );
}
